// Scratch-off (instant) lottery tracking: reading a pack/ticket barcode, counting tickets sold from the position of each active pack,
// and comparing what the packs say was sold with what the register recorded.

/** How the digits of a ticket barcode split into game, pack and ticket. Defaults to 4 + 6 + 3; stores can change it. */
export interface BarcodeLayout { gameDigits: number; packDigits: number; ticketDigits: number }
export const DEFAULT_LAYOUT: BarcodeLayout = { gameDigits: 4, packDigits: 6, ticketDigits: 3 };

export interface ScannedTicket { gameNo: string; packNo: string; ticket: number }

export function parseTicketBarcode(raw: string, layout: BarcodeLayout = DEFAULT_LAYOUT): ScannedTicket | null {
  const d = raw.replace(/\D/g, '');
  const need = layout.gameDigits + layout.packDigits + layout.ticketDigits;
  if (d.length < need) return null;
  const g = layout.gameDigits; const p = g + layout.packDigits;
  return { gameNo: d.slice(0, g), packNo: d.slice(g, p), ticket: Number(d.slice(p, need)) };
}

/** Whether a pack is sold from ticket 0 upwards or from its last ticket downwards. */
export type Direction = 'asc' | 'desc';

/** Position of the first ticket to sell in a new pack. */
export const startPosition = (dir: Direction, ticketsPerPack: number) => (dir === 'asc' ? 0 : ticketsPerPack - 1);
/** Position once every ticket in the pack is gone. */
export const endPosition = (dir: Direction, ticketsPerPack: number) => (dir === 'asc' ? ticketsPerPack : -1);

/**
 * Tickets sold when the front of a pack moves from one position to another (the position is the next ticket to sell).
 * A negative result means the pack went backwards, which is an error to flag.
 */
export function ticketsSold(dir: Direction, from: number, to: number): number {
  return dir === 'asc' ? to - from : from - to;
}

export const validPosition = (position: number, ticketsPerPack: number) => Number.isInteger(position) && position >= 0 && position < ticketsPerPack;

export type Standing = 'even' | 'over' | 'short';

/** Recorded at the register minus what the packs say was sold. Negative = the drawer is short. */
export function reconcile(countedCents: number, recordedCents: number): { varianceCents: number; standing: Standing } {
  const varianceCents = recordedCents - countedCents;
  return { varianceCents, standing: varianceCents === 0 ? 'even' : varianceCents > 0 ? 'over' : 'short' };
}

/**
 * Settlement with the state. Sales minus payouts is owed; the state takes its payment and the store keeps the difference as commission.
 * Returns the commission, or an error when the state's debit is more than the period's net (something is missing).
 */
export function settlement(salesCents: number, payoutsCents: number, stateDebitCents: number): { netCents: number; commissionCents: number; problem: string | null } {
  const netCents = salesCents - payoutsCents;
  const commissionCents = netCents - stateDebitCents;
  return { netCents, commissionCents, problem: commissionCents < 0 ? 'The state debited more than your sales minus payouts. Check for missing sales or a wrong amount.' : null };
}
